import React, { useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../App';
import proxyClient, { getAccessToken } from '../utils/proxyClient';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [hasAccess, setHasAccess] = React.useState(false);

  React.useEffect(() => {
    // Check if logout is in progress
    const logoutInProgress = sessionStorage.getItem('logoutInProgress') === 'true';
    if (logoutInProgress) {
      console.log("ProtectedRoute: Logout in progress, redirecting to login");
      sessionStorage.removeItem('logoutInProgress');
      setHasAccess(false);
      setLoading(false);
      navigate('/login');
      return;
    }
    
    // Check if authentication navigation is in progress
    const authInProgress = sessionStorage.getItem('authNavigationInProgress') === 'true';
    if (authInProgress) {
      // Clear the flag
      console.log("ProtectedRoute: Auth navigation in progress, waiting for user data");
      sessionStorage.removeItem('authNavigationInProgress');
      // Don't redirect yet, wait for auth to complete
      return;
    }
    
    const checkAccess = async () => {
      console.log('ProtectedRoute: Checking access, user:', user ? 'exists' : 'null');
      
      // Force an immediate session check to ensure we have the latest status 
      // This helps immediately detect logout
      if (!authLoading) {
        try {
          const { data: sessionData } = await proxyClient.auth.getSession();
          const sessionUser = sessionData?.session?.user;
          
          // If we detect no session but still have a user in context, redirect immediately
          if (!sessionUser && user) {
            console.log('ProtectedRoute: Session check shows user logged out - immediate redirect to login');
            setHasAccess(false);
            setLoading(false);
            navigate('/login', { state: { from: location } });
            return;
          }
        } catch (error) {
          console.error('ProtectedRoute: Error checking session', error);
        }
      }
      
      if (!user) {
        console.log('ProtectedRoute: No user, redirecting to login');
        setHasAccess(false);
        setLoading(false);
        navigate('/login', { state: { from: location } });
        return;
      }

      if (requireAdmin) {
        // Check admin status using backend API
        try {
          console.log('ProtectedRoute: User exists, checking admin status for:', user.id);
          
          const token = getAccessToken();
          const response = await fetch('/api/auth/profile', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            console.error('ProtectedRoute: Error fetching profile:', response.statusText);
            setHasAccess(false);
            setLoading(false);
            return;
          }
          
          const profileData = await response.json();
          const isAdminUser = profileData?.role === 'admin';
          console.log('ProtectedRoute: Admin check result:', isAdminUser, 'profile data:', profileData);
          setHasAccess(isAdminUser);
        } catch (error) {
          console.error('ProtectedRoute: Error checking admin status', error);
          setHasAccess(false);
        }
      } else {
        console.log('ProtectedRoute: Admin not required, granting access');
        setHasAccess(true);
      }
      setLoading(false);
    };

    checkAccess();
  }, [user, requireAdmin, navigate, location, authLoading]);

  if (loading || authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    console.log('ProtectedRoute: Access denied, redirecting to login');
    return <Navigate to="/login" />;
  }

  console.log('ProtectedRoute: Access granted');
  return <>{children}</>;
};

export default ProtectedRoute; 